<template>
    <div>
        <!-- Auto-submit full-page loader overlay -->
        <div v-if="autoSubmitInProgress" class="auto-submit-overlay">
            <div class="auto-submit-message">
                <div class="spinner"></div>
                <p>Processing your booking...</p>
            </div>
        </div>

        <!-- Hide entire content when auto-submit is in progress -->
        <template v-if="!autoSubmitInProgress">
            <section class="tabs-section">
                <NavBtns @navigatetotab="navigateToTab" :combo-ids="comboIds" :tabs="tabs" />
                <FlowNavigation currentStep="Init" formId="tour-schedule-form" @submit="submit" />
            </section>

            <section class="inner-content-section py-9">
                <div class="container">
                    <TourForm ref="TourForm" :form="form" :form-date="formDate" :begins="begins" :slot-not-found="slotNotFound"
                        :date-time-arr="dateTimeArr" :reveal="reveal" :tour-package-name="tourPackageName"
                        :hotels="hotels" :errors="errors" :is_group_rate_enabled="is_group_rate_enabled"
                        :is_partner_package="is_partner_package"
                        :tour-package-rate-groups="details.tourPackageRateGroups"
                        :tour-package-data="details.tourPackageData"
                        :selectgrouppeoples="selectgrouppeoples" :has_contacts="has_contacts"
                        :static-date-range="staticDateRange" :selected-date="selectedDate"
                        :call-to-book-validation="callToBookValidation" :open-phone-popup="openPhonePopup"
                        :selected-slot="selectedSlot" :is-disabled="isDisabled"
                        :handle-group-rate-discount-change="handleGroupRateDiscountChange"
                        :increment="increment" :decrement="decrement" :update-pax-detail="updatePaxDetail"
                        :remove-pax="removePax"
                        :submit="submit" />

                    <!-- Package details removed from Schedule section as per user request -->
                    <TourDetails :tour-package-data="details.tourPackageData" :form="form"
                        :things-to-bring="details.thingsToBring" />
                </div>
            </section>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import { getMomentTimezone } from '../utils/dateUtils';
import Swal from 'sweetalert2';
import NavBtns from './Nav/NavBtns.vue';
import FlowNavigation from './Nav/FlowNavigation.vue';
import TourForm from './Initialization/TourForm.vue';
import TourDetails from './Initialization/TourDetails.vue';
import moment from 'moment';

export default {
    name: 'Init',
    title: 'Native American Tours',
    components: {
        NavBtns,
        FlowNavigation,
        TourForm,
        TourDetails
    },
    data() {
        return {
            baseUrl: process.env.VUE_APP_BASE_URL,
            iframeStatus: true,
            reveal: false,
            loaderInstance: null,
            packageOrder: [],
            comboIds: 0,
            tourPackageName: '',
            totalAvailableSeats: 0,
            selectgrouppeoples: [],
            details: { tourPackageData: [], tourPackageRateGroups: {}, thingsToBring: [] },
            hotels: [],
            cartItem: {},
            cartItemLength: 0,
            slotNotFound: false,
            customRateFound: false,
            dateTimeArr: [],
            begins: null,
            blockedTimes: {},
            errors: [],
            hasCustomFields: false,
            autoSubmitInProgress: false,
            form: {
                tenant_id: '',
                tour_operator_id: 0,
                package_id: 0,
                package_name: '',
                package_image: '',
                affiliate_id: 0,
                hotel_id: 0,
                hotel_name: '',
                hotel_image: '',
                hotel_address: '',
                timezone: this.$store.state.timezone,
                date: null,
                time_date: null,
                total_people_selected: 0,
                people_group: [],
                rates: [],
                rate_group: [],
                rate_group_ids: [],
                infant_count: 0,
                calculation: [],
                subtotal: 0,
                fees: 0,
                total: 0,
                addons_total: 0,
                addons_fee: 0,
                tour_slot_id: 0,
                service_commission: 0,
                tour_promotion_id: 0,
                discount2_percentage: 0,
                discount2_value: 0,
                things_to_bring: [],
                short_description: '',
                long_description: '',
                duration: '',
                slot_time: null,
                latitude: null,
                longitude: null,
                type: 'Tour',
                travel_duration: '02:00:00',
                custom_fields: null,
                before_discount_subtotal: 0,
                before_discount_fees: 0,
                before_discount_total: 0,
                selectedRateId: null,
                selectedSize: null,
                selectedRate: 0,
                selectedTax: 0,
                package_has_slots: 1,
                tax_applicable: 1,
                show_seat_availability: 1,
                block_ctb_duration: 0,
                ctb_description: '',
                disable_slot_overlap_blocking: false,
                call_to_book: false,
                phone_number: '',
                counters: {},
                paxDetails: {},
                currency: "USD"
            },
            minSeats: 0,
            maxSeats: 0,
            is_group_rate_enabled: 0,
            is_partner_package: 0,
            withRateGroups: 1,
            has_contacts: 0,
            hasDirectUrl: false,
        };
    },
    computed: {
        tabs() {
            // Choose Date is always step 2 (for both single and combo packages)
            return 2;
        },
        formDate() {
            return this.getFormDate();
        }
    },
    created() {
        this.initializeFromStore();
        // Use formDate computed property to get current date from store
        this.fetchTourData(this.formDate, false);
        this.$store.dispatch('storeTabs', this.tabs);
        this.$store.dispatch('storeMindChange', 0);
    },
    watch: {
        '$route.query': {
            handler(newQuery) {
                // Check if we have all required URL query parameters
                const hasDate = newQuery.date !== null && newQuery.date !== undefined;

                const hastid = newQuery.tid !== null && newQuery.tid !== undefined && newQuery.tid == 'dixies';

                const hasTimeSlot = newQuery['time-slot'] !== null && newQuery['time-slot'] !== undefined;

                const hasRateGroupIds = Object.keys(newQuery).some(key => key.startsWith('rategroupid['));
                
                if (hasDate && hasTimeSlot && hasRateGroupIds) {
                    this.autoSubmitInProgress = true;
                } else {
                    this.autoSubmitInProgress = false;
                }

                if (hasDate || hastid) {
                    this.hasDirectUrl = true;
                }
            },
            immediate: true
        }
    },
    methods: {
        getFormDate() {
            // Priority: Use date from store (which includes URL date) if available
            if (this.$store.state.date) {
                return this.$store.state.date;
            }
            
            const today = getMomentTimezone(this.$store.state.timezone);
            if (this.$store.state.tenantId === 'dixies' && today.year() === 2025) {
                return getMomentTimezone(this.$store.state.timezone, [2026, 0, 1]).format('YYYY-MM-DD');
            }
            return getMomentTimezone(this.$store.state.timezone).format('YYYY-MM-DD');
        },
        initializeFromStore() {
            const { state } = this.$store;
            this.packageOrder = state.packageOrder || [];
            this.comboIds = state.comboIds ?? 0;
            this.cartItem = state.cartItem || {};
            this.cartItemLength = Object.keys(this.cartItem).length;
            this.iframeStatus = state.iframeStatus;

            // Load form data based on package type and navigation source
            let storedForm;
            if (this.comboIds) {
                // For combo packages: prefer formData (when editing from Index),
                // but fallback to cart item if formData is null (when navigating via nav menu)
                storedForm = state?.formData || state?.cartItem?.[state.slotId];
            } else {
                // For single packages: load from cart item
                storedForm = state?.cartItem?.[state.slotId];
            }
            const defaultForm = {
                tenant_id: state.tenantId || null,
                tour_operator_id: state.tourOperatorId || 0,
                package_id: state.packageId || 0,
                affiliate_id: state.affiliateId || 0,
                tour_slot_id: 0,
                time_date: null,
                slot_time: null,
                people_group: [],
                selectedRateId: null,
                selectedSize: null,
                selectedRate: 0,
                selectedTax: 0,
                package_has_slots: 1,
                tax_applicable: 1,
                show_seat_availability: 1,
                block_ctb_duration: 0,
                ctb_description: '',
                call_to_book: false,
                disable_slot_overlap_blocking: false
            };

            this.form = storedForm && storedForm.package_id === state.packageId && storedForm.affiliate_id === state.affiliateId
                ? { ...this.form, ...storedForm }
                : { ...this.form, ...defaultForm };
        },

        async fetchTourData(date, resetSlot = false) {
            this.processLoader(true);
            try {
                const { package_id, affiliate_id } = this.form;
                const slotResponse = await axios.get(`/tour-slot/${date}/${package_id}/${affiliate_id}`);
                const { begins, Time: slotTimes = [], closedSlotVisibility, TotalAvailableSeats = 0 } = slotResponse.data;

                this.begins = begins;
                this.dateTimeArr = (closedSlotVisibility === false
                    ? slotTimes.filter(slot => slot.bookable_status === 'Open' && slot.dd < slot.seats)
                    : slotTimes
                ).map(slot => ({ ...slot, visible: true, custom_rate: slot.custom_rate || 0 }));

                this.customRateFound = this.dateTimeArr.some(slot => slot.custom_rate !== 0);
                this.totalAvailableSeats = TotalAvailableSeats;
                this.maxSeats = TotalAvailableSeats;
                this.slotNotFound = this.dateTimeArr.length === 0;

                this.selectgrouppeoples = Array.from({ length: TotalAvailableSeats + 1 }, (_, i) => ({
                    id: i + 1,
                    value: i,
                    number: i
                }));

                if (!begins && !this.staticDateRange(this.formDate, this.form.tenant_id)) {
                    let revealFlag = (this.customRateFound == true && this.form.tour_slot_id === 0) ? false : true;
                    await this.fetchPackageData(date, resetSlot, revealFlag);
                } else if (this.form.tour_slot_id !== 0) {
                    await this.fetchPackageData(date, resetSlot, true);
                } else {
                    this.reveal = this.customRateFound;
                    this.tourPackageName = this.customRateFound ? '' : this.tourPackageName;
                }
            } catch (error) {
                console.error('Failed to fetch tour data:', error);
                this.slotNotFound = true;
                this.details.tourPackageRateGroups = {};
                this.begins = error?.response?.data?.data?.begins || null;
            } finally {
                this.processLoader(false);
            }
        },

        async fetchPackageData(date, resetSlot, revealFlag) {
            this.processLoader(true);
            try {
                const { tour_operator_id, package_id, affiliate_id, tour_slot_id } = this.form;
                const response = await axios.get(
                    `/tour-package/${date}/${tour_operator_id}/${package_id}/${affiliate_id}/0/${this.withRateGroups}/${tour_slot_id}`
                );
                this.$store.dispatch('storeTourPackage', response.data);
                this.$store.dispatch('storeCustomCheckoutTerms', response.data.customCheckoutTerms || []);

                const tourData = response.data.tourPackageData?.[0] || {};
                this.$store.dispatch('storeCurrency', tourData.currency || "USD");
                this.$store.dispatch('storeCountryCode', tourData.country_code || "US");

                this.tourPackageName = tourData.package_name || '';
                this.details = {
                    tourPackageData: response.data.tourPackageData || [],
                    tourPackageRateGroups: response.data.tourPackageRateGroups?.[package_id] || [],
                    thingsToBring: response.data.thingsToBring || []
                };
                this.hotels = response.data.hotels || [];
                this.has_contacts = tourData.has_contacts || 0;

                this.hasCustomFields = response.data.hasCustomFields || false;
                this.$store.dispatch('storeHasCustomFields', this.hasCustomFields);

                Object.assign(this.form, {
                    package_image: tourData.FrontendPackageImage || '',
                    things_to_bring: response.data.thingsToBring || [],
                    short_description: tourData.short_description || '',
                    long_description: tourData.long_description || '',
                    duration: tourData.duration || '',
                    latitude: tourData.latitude || null,
                    longitude: tourData.longitude || null,
                    type: tourData.type || 'Tour',
                    travel_duration: tourData.travel_duration || '02:00:00',
                    phone_number: tourData.phone_number || '',
                    package_has_slots: tourData.package_has_slots ?? 1,
                    tax_applicable: tourData.tax_applicable ?? 1,
                    show_seat_availability: tourData.show_seat_availability ?? 1,
                    block_ctb_duration: tourData.block_ctb_duration || 0,
                    ctb_description: tourData.ctb_description || '',
                    disable_slot_overlap_blocking: tourData.disable_slot_overlap_blocking ?? false,
                    call_to_book: response.data.callToBook || false,
                    service_commission: affiliate_id
                        ? Number(tourData.affiliate_processing_percentage || 0)
                        : Number(tourData.service_commission_percentage || 0)
                });

                this.is_group_rate_enabled = tourData.is_group_rate_enabled || 0;
                this.is_partner_package = tourData.is_partner_package || 0;
                if (this.is_group_rate_enabled && this.details.tourPackageRateGroups.length > 0) {
                    const defaultGroup = this.details.tourPackageRateGroups[0];
                    
                    // Set default values if not already set
                    if (!this.form.selectedRateId) {
                        this.form.selectedRateId = defaultGroup.id;
                    }
                    if (!this.form.selectedSize) {
                        this.form.selectedSize = Number(defaultGroup.size);
                    }
                    if (!this.form.selectedRate) {
                        this.form.selectedRate = Number(defaultGroup.rate || 0);
                    }
                    if (!this.form.selectedTax) {
                        this.form.selectedTax = Number(defaultGroup.tax || 0);
                    }
                    
                    // Update total people selected and pricing
                    this.form.total_people_selected = Number(this.form.selectedSize);
                    this.updatePricingBreakdown();
                }

                if (this.comboIds && this.cartItemLength && !this.form.disable_slot_overlap_blocking) {
                    this.updateBlockedTimes(date);
                }

                if (this.form.package_has_slots === 0 && this.dateTimeArr[0]) {
                    this.selectedSlot(this.dateTimeArr[0].Id, this.dateTimeArr[0].Time, this.dateTimeArr[0].slot_time);
                }

                // Handle URL parameters for time-slot and rategroupid
                this.applyUrlParameters();

                this.reveal = revealFlag;
            } catch (error) {
                console.error('Failed to fetch package data:', error);
                this.errors.push('Failed to load tour package data.');
            } finally {
                if (resetSlot) {
                    Object.assign(this.form, {
                        tour_slot_id: 0,
                        time_date: null,
                        slot_time: null,
                        people_group: [],
                        counters: {},
                        total_people_selected: 0,
                        paxDetails: {}
                    });
                }
                this.processLoader(false);
            }
        },

        updateBlockedTimes(date) {
            this.blockedTimes = Object.values(this.cartItem).reduce((acc, item) => {
                if (item.package_id !== this.form.package_id) {
                    const [hours, minutes] = item.travel_duration.split(':').map(Number);
                    const timeBefore = this.calculateTime(item.slot_time, -hours, -minutes);
                    const timeAfter = this.calculateTime(item.slot_time, hours, minutes);

                    acc[item.package_id] = {
                        date: item.date,
                        time: [timeBefore, timeAfter],
                        package_has_slots: item.package_has_slots
                    };
                }
                return acc;
            }, {});

            this.dateTimeArr = this.dateTimeArr.filter(slot => {
                return !Object.values(this.blockedTimes).some(({ date: blockedDate, time, package_has_slots }) => {
                    if (blockedDate !== date || !package_has_slots) return false;
                    const [startTime, endTime] = time.map(t => getMomentTimezone(this.$store.state.timezone, `2000-01-01T${t}`).format('YYYY-MM-DD HH:mm:ss'));
                    const slotTime = getMomentTimezone(this.$store.state.timezone, `2000-01-01T${slot.slot_time}`).format('YYYY-MM-DD HH:mm:ss');
                    return slotTime >= startTime && slotTime <= endTime;
                });
            });
        },

        selectedDate(date) {
            this.reveal = false;
            this.customRateFound = false;
            Object.assign(this.form, {
                tour_slot_id: 0,
                time_date: null,
                slot_time: null,
                people_group: [],
                counters: {},
                total_people_selected: 0,
                paxDetails: {},
                subtotal: 0,
                fees: 0,
                total: 0,
                before_discount_subtotal: 0,
                before_discount_fees: 0,
                before_discount_total: 0,
                calculation: [],
                rates: [],
                rate_group: [],
                rate_group_ids: [],
                infant_count: 0,
            });
            this.dateTimeArr = [];
            this.errors = [];
            this.$store.dispatch('storeDate', date);
            this.fetchTourData(date, true);
        },

        handleGroupRateDiscountChange() {
            const selectedSize = Number(this.form.selectedSize);
            const selectedGroup = this.details.tourPackageRateGroups.find(
                group => Number(group.size) === selectedSize
            );

            if (selectedGroup) {
                Object.assign(this.form, {
                    selectedRateId: selectedGroup.id,
                    selectedSize: selectedSize,
                    selectedRate: Number(selectedGroup.rate),
                    selectedTax: Number(selectedGroup.tax),
                    total_people_selected: selectedSize
                });
                
                // Update pricing breakdown immediately when rate group changes
                this.updatePricingBreakdown();
            } else {
                console.error('No matching group found for selectedSize:', selectedSize);
            }
        },

        updatePricingBreakdown() {
            // Only calculate if we have rate groups
            if (!this.details.tourPackageRateGroups?.length) {
                this.form.subtotal = 0;
                this.form.fees = 0;
                this.form.total = 0;
                return;
            }

            // For group rate enabled, check if selectedSize is set
            if (this.is_group_rate_enabled) {
                if (!this.form.selectedSize || this.form.selectedSize === 0) {
                    this.form.subtotal = 0;
                    this.form.fees = 0;
                    this.form.total = 0;
                    return;
                }
            } else {
                // For regular rates, check if any people are selected
                if (this.form.total_people_selected === 0) {
                    this.form.subtotal = 0;
                    this.form.fees = 0;
                    this.form.total = 0;
                    return;
                }
            }

            const rateGroups = Array.isArray(this.details.tourPackageRateGroups) 
                ? this.details.tourPackageRateGroups 
                : Object.values(this.details.tourPackageRateGroups);
            const { paxSubtotalArr, feesGroupArr } = this.calculateTotals(rateGroups);
            
            // Calculate subtotals and fees
            let subtotalSum = 0;
            let feesSum = 0;

            if (this.is_group_rate_enabled) {
                // For group rates, use the first element (single group selection)
                subtotalSum = Number(paxSubtotalArr[0] || 0);
                feesSum = Number(feesGroupArr[0] || 0);
                
            } else {
                // For regular rates, sum only rate groups that have people (count > 0)
                rateGroups.forEach((group, index) => {
                    const count = this.form.counters[group.id] || 0;
                    if (count > 0) {
                        subtotalSum += Number(paxSubtotalArr[index] || 0);
                        feesSum += Number(feesGroupArr[index] || 0);
                    }
                });
            }

            this.form.before_discount_subtotal = Number(subtotalSum);
            this.form.before_discount_fees = Number(feesSum);
            this.form.before_discount_total = this.roundout(subtotalSum + feesSum);

            let discountedSubtotal = subtotalSum;
            if (this.form.discount2_percentage > 0) {
                const discount = (subtotalSum * this.form.discount2_percentage) / 100;
                this.form.discount2_value = Number(discount).toFixed(2);
                discountedSubtotal = Number(subtotalSum - discount).toFixed(2);
                feesSum = this.roundout((discountedSubtotal * this.form.service_commission) / 100, 2);
            } else if (this.form.discount2_value > 0) {
                discountedSubtotal = Number(subtotalSum - this.form.discount2_value).toFixed(2);
                feesSum = this.roundout((discountedSubtotal * this.form.service_commission) / 100, 2);
            }

            const total = Number(discountedSubtotal) + Number(feesSum);
            this.form.subtotal = this.roundout(discountedSubtotal);
            this.form.fees = feesSum;
            this.form.total = this.roundout(total);
        },

        async submit() {
            this.errors = [];
            this.processLoader(true);

            try {
                if (this.has_contacts) {
                    const validationErrors = this.validatePaxDetails();
                    if (validationErrors.length) {
                        this.errors = validationErrors;
                        this.$nextTick(() => this.scrollToErrors());
                        return;
                    }
                }

                if (!this.form.time_date && this.form.package_has_slots) {
                    this.errors.push('Please select a start time for your tour.');
                }

                // Use common processing logic
                await this.processFormSubmission();

                if (this.errors.length) {
                    this.$nextTick(() => this.scrollToErrors());
                }
            } catch (error) {
                console.error('Submit error:', error);
                this.errors.push('An unexpected error occurred. Please try again.');
                this.$nextTick(() => this.scrollToErrors());
            } finally {
                this.processLoader(false);
            }
        },

        async processFormSubmission() {
            const rateGroups = Array.isArray(this.details.tourPackageRateGroups) 
                ? this.details.tourPackageRateGroups 
                : Object.values(this.details.tourPackageRateGroups);
            if (this.validateMinMaxScenario(rateGroups)) {
                this.$nextTick(() => this.scrollToErrors());
                return;
            }

            const { groupPaxArr, ratePaxArr, rateGroupArr, rateGroupIdArr, paxSubtotalArr, feesGroupArr, infantCount } = this.calculateTotals(rateGroups);

            // Calculate totals based on rate type
            let subtotalSum = 0;
            let feesSum = 0;

            if (this.is_group_rate_enabled) {
                subtotalSum = Number(paxSubtotalArr[0] || 0);
                feesSum = Number(feesGroupArr[0] || 0);
            } else {
                rateGroups.forEach((group, index) => {
                    const count = this.form.counters[group.id] || 0;
                    if (count > 0) {
                        subtotalSum += Number(paxSubtotalArr[index] || 0);
                        feesSum += Number(feesGroupArr[index] || 0);
                    }
                });
            }

            this.form.before_discount_subtotal = Number(subtotalSum);
            this.form.before_discount_fees = Number(feesSum);
            this.form.before_discount_total = this.roundout(subtotalSum + feesSum);

            let discountedSubtotal = subtotalSum;
            if (this.form.discount2_percentage > 0) {
                const discount = (subtotalSum * this.form.discount2_percentage) / 100;
                this.form.discount2_value = Number(discount).toFixed(2);
                discountedSubtotal = Number(subtotalSum - discount).toFixed(2);
                feesSum = this.roundout((discountedSubtotal * this.form.service_commission) / 100, 2);
            } else if (this.form.discount2_value > 0) {
                discountedSubtotal = Number(subtotalSum - this.form.discount2_value).toFixed(2);
                feesSum = this.roundout((discountedSubtotal * this.form.service_commission) / 100, 2);
            }

            const total = Number(discountedSubtotal) + Number(feesSum);
            this.form.subtotal = this.roundout(discountedSubtotal);
            this.form.fees = feesSum;
            this.form.total = this.roundout(total);

            if (this.form.total <= 0) {
                if (this.is_group_rate_enabled && (!this.form.selectedSize || this.form.selectedSize === 0)) {
                    this.errors.push('Please select the number of guests from the dropdown.');
                } else if (!this.is_group_rate_enabled && this.form.total_people_selected === 0) {
                    this.errors.push('Please select the number of guests for your tour.');
                } else {
                    this.errors.push('Unable to calculate total price. Please refresh and try again.');
                }
                this.$nextTick(() => this.scrollToErrors());
                return;
            }

            if (!this.errors.length) {
                // Return all counters (including 0 values) to maintain array alignment with backend
                const cleanedCounters = this.getCleanedCounters();

                Object.assign(this.form, {
                    date: this.formDate,  // Ensure date is set in form
                    calculation: paxSubtotalArr,
                    rates: ratePaxArr,
                    rate_group: rateGroupArr,
                    rate_group_ids: rateGroupIdArr,
                    infant_count: infantCount,
                    people_group: groupPaxArr,
                    package_name: this.tourPackageName,
                    counters: cleanedCounters
                });
                
                // Update store with final form data before adding to cart
                this.$store.dispatch('storeFormData', { ...this.form });
                this.$store.dispatch('storeRateGroupCounters', { ...cleanedCounters });
                
                await this.addToCart();
            }
        },

        getCleanedCounters() {
            // Return all counters (including 0 values) to maintain array alignment with backend
            const allCounters = {};

            if (this.is_group_rate_enabled) {
                const selectedSize = Number(this.form.selectedSize);
                const selectedGroup = this.details.tourPackageRateGroups.find(
                    group => Number(group.size) === selectedSize
                );
                if (selectedGroup) {
                    allCounters[selectedGroup.id] = selectedSize;
                }
            } else {
                // For regular rates, use the counters object
                this.details.tourPackageRateGroups.forEach(group => {
                    allCounters[group.id] = this.form.counters[group.id] || 0;
                });
            }

            return allCounters;
        },

        validatePaxDetails() {
            const errors = [];
            this.details.tourPackageRateGroups.forEach(tour => {
                this.form.paxDetails[tour.id] = this.form.paxDetails[tour.id] || [];
                if (this.form.counters[tour.id] && this.form.paxDetails[tour.id].length !== this.form.counters[tour.id]) {
                    errors.push(`Please provide details for all ${this.form.counters[tour.id]} attendees in ${tour.rate_for}.`);
                } else {
                    this.form.paxDetails[tour.id].forEach((pax, index) => {
                        if (!pax.name || !pax.age || !pax.weight) {
                            errors.push(`Please fill in all fields for Attendee ${index + 1} in ${tour.rate_for}.`);
                        }
                    });
                }
            });
            return errors;
        },

        calculateTotals(rateGroups) {
            const groupPaxArr = [];
            const ratePaxArr = [];
            const rateGroupArr = [];
            const rateGroupIdArr = [];
            const paxSubtotalArr = [];
            const feesGroupArr = [];
            let infantCount = 0;

            if (this.is_group_rate_enabled) {
                const selectedSize = Number(this.form.selectedSize);
                const selectedGroup = rateGroups.find(group => Number(group.size) === selectedSize);
                if (selectedGroup) {
                    groupPaxArr.push(selectedSize);
                    ratePaxArr.push(Number(selectedGroup.rate));
                    rateGroupArr.push(selectedGroup.rate_for);
                    rateGroupIdArr.push(selectedGroup.id);
                    // Calculate subtotal: rate + permit_fee + tax for the entire group
                    const baseRate = Number(selectedGroup.rate || 0);
                    const permitFee = Number(selectedGroup.permit_fee || 0);
                    const tax = Number(selectedGroup.tax || 0);
                    const partnerFee = this.is_partner_package ? Number(selectedGroup.partner_fee_amount || 0) : 0;
                    const subtotal = (baseRate + permitFee + tax + partnerFee).toFixed(2);
                    paxSubtotalArr.push(subtotal);
                    // Calculate fees based on the subtotal
                    const fees = this.roundout((Number(subtotal) * Number(this.form.service_commission)) / 100, 2);
                    feesGroupArr.push(fees.toFixed(2));
                }
            } else {
                rateGroups.forEach((group, index) => {
                    const partnerFeeAmount = this.is_partner_package ? group.partner_fee_amount : 0;
                    const count = this.form.counters[group.id] || 0;
                    
                    // Include ALL rate groups to maintain array index alignment with backend
                    groupPaxArr.push(count);
                    ratePaxArr.push(group.rate);
                    rateGroupArr.push(group.rate_for);
                    rateGroupIdArr.push(group.id);

                    // Check if this group is infant rate and add to infant count
                    if (group.is_infant_rate === 1 && count > 0) {
                        infantCount += count;
                    }

                    const rate = Number(group.rate) + Number(group.permit_fee || 0) + Number(group.tax || 0) + Number(partnerFeeAmount || 0);
                    const subtotal = (count * rate).toFixed(2);
                    paxSubtotalArr.push(subtotal);
                    const fees = this.roundout((Number(rate) * Number(this.form.service_commission)) / 100, 2);
                    feesGroupArr.push((count * fees).toFixed(2));
                });
            }

            const result = { groupPaxArr, ratePaxArr, rateGroupArr, rateGroupIdArr, paxSubtotalArr, feesGroupArr, infantCount };

            return result;
        },

        validateMinMaxScenario(groups) {
            if (this.is_group_rate_enabled) return false;

            const groupPaxArr = groups.map(group => this.form.counters[group.id] || 0);
            const totalPax = groupPaxArr.reduce((sum, val) => sum + Number(val), 0);

            if (totalPax === 0) {
                this.errors.push('Please select your group of people for the tour.');
            } else {
                groups.forEach(group => {
                    const count = this.form.counters[group.id] || 0;
                    if (group.min_pax_allowed > count) {
                        this.errors.push(
                            `Please select a minimum of ${group.min_pax_allowed} ${group.rate_for} people to process your booking.`
                        );
                    }
                    if (group.max_pax_allowed && group.max_pax_allowed < count) {
                        this.errors.push(
                            `Please select a maximum of ${group.max_pax_allowed} ${group.rate_for} people to process your booking.`
                        );
                    }
                });
            }

            return this.errors.length > 0;
        },

        async addToCart() {
            try {
                // Get selected rate group IDs and infant count
                const { selectedIds: selectedRateGroupIds, infantCount } = this.getSelectedRateGroupIds();

                const checkSlot = {
                    tour_slot_id: this.form.tour_slot_id,
                    package_id: this.form.package_id,
                    affiliate_id: this.form.affiliate_id,
                    tourists: this.form.people_group,
                    tour_slot_time: this.form.time_date,
                    rate_group_ids: selectedRateGroupIds,
                    infants: infantCount
                };

                const response = await axios.post('/available-seats', checkSlot);
                if (response.data.success === 'false') {
                    this.errors.push(response.data.message);
                    this.$nextTick(() => this.scrollToErrors());
                    return;
                }

                // For combo packages, preserve all existing cart items except the current package being updated
                // For single packages, start with empty cart
                let updatedCart = this.comboIds === 0
                    ? {}
                    : Object.fromEntries(
                        Object.entries(this.cartItem).filter(([slotId, item]) => {
                            return item.package_id !== this.form.package_id;
                        })
                    );

                // Set package image and add current form to cart
                this.form.package_image = this.details.tourPackageData?.[0]?.FrontendPackageImage || '';
            
                // Create a deep copy of the form to ensure pricing data is preserved
                const formData = {
                    ...this.form,
                    subtotal: this.form.subtotal,
                    fees: this.form.fees,
                    total: this.form.total,
                    before_discount_subtotal: this.form.before_discount_subtotal,
                    before_discount_fees: this.form.before_discount_fees,
                    before_discount_total: this.form.before_discount_total,
                    total_people_selected: this.form.total_people_selected,
                    selectedSize: this.form.selectedSize,
                    selectedRate: this.form.selectedRate,
                    selectedTax: this.form.selectedTax,
                    selectedRateId: this.form.selectedRateId
                };
                
                updatedCart[this.form.tour_slot_id] = formData;
                this.cartItem = updatedCart;
                this.$store.dispatch('storeCartItem', this.cartItem);
                this.$store.dispatch('storeMindChange', 1);

                // Dynamic routing based on whether company has add-ons and package type
                const hasCustomFields = this.$store.state.hasCustomFields;
                const isComboPackage = this.comboIds && this.comboIds !== 0;

                if (isComboPackage) {
                    // Combo packages
                    if (hasCustomFields === false) {
                        // No add-ons available, go back to Index to schedule more packages
                        this.$router.push({ name: 'Index' });
                    } else {
                        // Add-ons available, show add-ons first, then they'll go to Index after submission
                        this.$router.push({ name: 'Addons' });
                    }
                } else {
                    // Single package flow
                    if (hasCustomFields === false) {
                        // No add-ons available, go directly to Checkout
                        this.$router.push({ name: 'Checkout' });
                    } else {
                        // Add-ons available, go to Add Extras page
                        this.$router.push({ name: 'Addons' });
                    }
                }
            } catch (error) {
                console.error('Add to cart error:', error);
                this.errors.push(error.response?.data?.message || 'Failed to add to cart.');
                this.$nextTick(() => this.scrollToErrors());
            }
        },

        getSelectedRateGroupIds() {
            // Get rate group IDs for groups that have people selected (count > 0)
            const selectedIds = [];
            let infantCount = 0;

            if (!this.is_group_rate_enabled) {
                // For regular rate groups, get IDs where counter > 0
                this.details.tourPackageRateGroups.forEach(group => {
                    const count = this.form.counters[group.id] || 0;
                    if (count > 0) {
                        selectedIds.push(group.id);
                        // Check if this group is infant rate
                        if (group.is_infant_rate === 1) {
                            infantCount += count;
                        }
                    }
                });
            }

            return { selectedIds, infantCount };
        },

        selectedSlot(id, timeDate, slotTime) {
            this.$store.dispatch('storeSlotId', id);
            Object.assign(this.form, {
                tour_slot_id: id,
                time_date: timeDate,
                slot_time: slotTime
            });

            if (this.customRateFound) {
                this.processLoader(true);
                this.tourPackageName = '';
                this.details = { tourPackageData: [], tourPackageRateGroups: [], thingsToBring: [] };
                this.form.counters = {};
                this.form.people_group = [];
                this.form.total_people_selected = 0;
                this.form.paxDetails = {};
                this.errors = [];
                
                // Reset amount calculations when slot changes (for custom rates)
                Object.assign(this.form, {
                    subtotal: 0,
                    fees: 0,
                    total: 0,
                    before_discount_subtotal: 0,
                    before_discount_fees: 0,
                    before_discount_total: 0,
                    calculation: [],
                    rates: [],
                    rate_group: [],
                    rate_group_ids: [],
                    infant_count: 0
                });
                
                this.fetchPackageData(this.formDate, false, true);
            }
        },

        increment(rateId) {
            const newCount = (this.form.counters[rateId] || this.minSeats) + 1;
            if (newCount > this.maxSeats) return;

            this.form.counters[rateId] = newCount;
            if (this.has_contacts) {
                this.form.paxDetails[rateId] = this.form.paxDetails[rateId] || [];
                this.form.paxDetails[rateId].push({ name: '', age: '', weight: '', note: '' });
            }

            if (!this.is_group_rate_enabled) {
                this.form.total_people_selected = Object.values(this.form.counters).reduce(
                    (sum, num) => sum + num,
                    0
                );
            }

            // Update pricing breakdown immediately when rate count changes
            this.updatePricingBreakdown();
        },

        decrement(rateId) {
            const newCount = (this.form.counters[rateId] || this.minSeats) - 1;
            if (newCount < this.minSeats) return;

            this.form.counters[rateId] = newCount;
            if (this.has_contacts && this.form.paxDetails[rateId]?.length) {
                this.form.paxDetails[rateId].pop();
            }

            if (!this.is_group_rate_enabled) {
                this.form.total_people_selected = Object.values(this.form.counters).reduce(
                    (sum, num) => sum + num,
                    0
                );
            }

            // Check if we need to reset infant rate groups to 0
            this.checkAndResetInfantRateGroups();
            
            // Update pricing breakdown immediately when rate count changes
            this.updatePricingBreakdown();
        },

        checkAndResetInfantRateGroups() {
            // Check if there are any non-infant rate groups with people selected
            const hasNonInfantSelection = this.details.tourPackageRateGroups.some(group => {
                const count = this.form.counters[group.id] || 0;
                const isNonInfant = group.is_infant_rate !== 1 && count > 0;
                return isNonInfant;
            });

            // If no non-infant selections, reset all infant rate group counters to 0
            if (!hasNonInfantSelection) {
                this.details.tourPackageRateGroups.forEach(group => {
                    if (group.is_infant_rate === 1) {
                        this.form.counters[group.id] = 0;
                        // Also clear pax details for infant groups
                        if (this.has_contacts && this.form.paxDetails[group.id]) {
                            this.form.paxDetails[group.id] = [];
                        }
                    }
                });
            }
        },

        updatePaxDetail(rateId, index, field, value) {
            if (this.form.paxDetails[rateId]?.[index]) {
                this.form.paxDetails[rateId][index][field] = value;
            }
        },

        removePax(rateId, index) {
            if (this.form.paxDetails[rateId]?.[index]) {
                this.form.paxDetails[rateId].splice(index, 1);
                this.form.counters[rateId] = (this.form.counters[rateId] || this.minSeats) - 1;
                if (!this.is_group_rate_enabled) {
                    this.form.total_people_selected = Object.values(this.form.counters).reduce(
                        (sum, num) => sum + num,
                        0
                    );
                }
            }
        },

        callToBookDuration(bookDuration, timeSlot) {
            if (!this.form.call_to_book || bookDuration === 0) return false;

            const timezone = this.$store.state.timezone;

            // Convert slot time to user's timezone for proper comparison
            const slotTime = getMomentTimezone(timezone, `${timeSlot.date}T${timeSlot.slot_time}`);

            // Get current time in user's timezone
            const currentTime = getMomentTimezone(timezone);

            // Calculate the time window: current time + bookDuration hours
            const callToBookWindowEnd = currentTime.clone().add(bookDuration, 'hours');

            // Show "CALL TO BOOK" if slot is within the next bookDuration hours
            return slotTime.isSameOrBefore(callToBookWindowEnd);
        },

        callToBookValidation(timeSlot, bool) {
            // 'Only by Phone' slots always show "CALL TO BOOK"
            if (timeSlot.bookable_status === 'Only by Phone') {
                return bool === true;
            }

            const isWithinCallToBookWindow = this.callToBookDuration(this.form.block_ctb_duration, timeSlot);

            // If within call-to-book window, show "CALL TO BOOK" regardless of slot status
            if (bool && isWithinCallToBookWindow) {
                return true;
            }

            // For showing seats left (bool = false), only show if NOT in call-to-book window
            // and slot is open with available seats
            if (!bool && !isWithinCallToBookWindow) {
                return timeSlot.bookable_status === 'Open' && timeSlot.dd < timeSlot.seats;
            }

            return false;
        },

        staticDateRange(date, tenant) {
            const tenants = ['kens', 'dixies'];
            if (!tenants.includes(tenant)) return false;

            const packageIds = tenant === 'kens' ? [1, 2, 6] : [1];
            const selectedDate = getMomentTimezone(this.$store.state.timezone, date).format('YYYY-MM-DD');
            const firstDate = getMomentTimezone(this.$store.state.timezone, '2026-01-12').format('YYYY-MM-DD');
            const secondDate = getMomentTimezone(this.$store.state.timezone, '2026-01-19').format('YYYY-MM-DD');
            const isClosed =
                selectedDate >= firstDate &&
                selectedDate < secondDate &&
                packageIds.includes(Number(this.form.package_id));

            if (isClosed || this.begins) {
                Object.assign(this.form, {
                    tour_slot_id: 0,
                    time_date: null,
                    slot_time: null,
                    people_group: []
                });
            }

            return isClosed;
        },

        async applyUrlParameters() {
            try {
                // Get URL parameters from localStorage
                const urlParams = JSON.parse(localStorage.getItem('urlParams') || '{}');
                
                const hasTimeSlot = urlParams.timeSlot !== null && urlParams.timeSlot !== undefined;
                const hasRateGroups = urlParams.rateGroupIds && Object.keys(urlParams.rateGroupIds).length > 0;
                // Clear time slot selection if not provided in URL
                if (this.hasDirectUrl &&!hasTimeSlot) {
                    this.form.tour_slot_id = 0;
                    this.form.time_date = null;
                    this.form.slot_time = null;
                }

                // Clear rate group selections if not provided in URL
                if (this.hasDirectUrl &&!hasRateGroups) {
                    this.form.counters = {};
                    this.form.paxDetails = {};
                    this.form.total_people_selected = 0;
                    this.form.people_group = [];
                    this.form.subtotal = 0;
                    this.form.fees = 0;
                    this.form.total = 0;
                    this.form.before_discount_subtotal = 0;
                    this.form.before_discount_fees = 0;
                    this.form.before_discount_total = 0;
                    this.form.calculation = [];
                    this.form.rates = [];
                    this.form.rate_group = [];
                    this.form.rate_group_ids = [];
                    this.form.infant_count = 0;
                }

                // If no URL parameters to process, return early
                if (!hasTimeSlot && !hasRateGroups) {
                    return;
                }
                
                // Validation flags
                let validationPassed = true;
                const validationErrors = [];
                
                // Validate and apply time slot if provided in URL
                let matchingSlot = null;
                if (hasTimeSlot) {
                    const timeSlotId = parseInt(urlParams.timeSlot);
                    
                    if (this.dateTimeArr.length === 0) {
                        validationErrors.push(`No time slots available for the selected date.`);
                        validationPassed = false;
                    } else {
                        matchingSlot = this.dateTimeArr.find(slot => slot.Id === timeSlotId);
                        
                        if (!matchingSlot) {
                            validationErrors.push(`Selected time slot not found or unavailable for the selected date.`);
                            validationPassed = false;
                        } else if (this.isDisabled(matchingSlot)) {
                            validationErrors.push(`Time slot ${matchingSlot.Time} is closed or sold out.`);
                            validationPassed = false;
                        } else if (this.callToBookValidation(matchingSlot, true)) {
                            validationErrors.push(`Time slot ${matchingSlot.Time} requires calling to book.`);
                            validationPassed = false;
                        } else {
                            // Auto-select the time slot
                            this.selectedSlot(matchingSlot.Id, matchingSlot.Time, matchingSlot.slot_time);
                        }
                    }
                }
                
                // Validate and apply rate group counters if provided in URL
                if (hasRateGroups && !this.is_group_rate_enabled) {
                    const rateGroupIds = urlParams.rateGroupIds;
                    const validRateGroupIds = [];
                    
                    // First pass: Validate all rate groups exist
                    for (const [rateGroupId, count] of Object.entries(rateGroupIds)) {
                        const id = parseInt(rateGroupId);
                        const counter = parseInt(count);
                        
                        // Check if this rate group exists in the current package
                        const rateGroup = this.details.tourPackageRateGroups.find(group => group.id === id);
                        if (!rateGroup) {
                            validationErrors.push(`Selected rate group does not exist for this package.`);
                            validationPassed = false;
                        } else if (counter < 0) {
                            validationErrors.push(`Invalid count ${counter} for rate group (${rateGroup.rate_for}).`);
                            validationPassed = false;
                        } else if (counter > this.maxSeats) {
                            validationErrors.push(`Count ${counter} exceeds maximum available seats (${this.maxSeats}) for ${rateGroup.rate_for}.`);
                            validationPassed = false;
                        } else if (counter > 0) {
                            validRateGroupIds.push({ id, counter, rateGroup });
                        }
                    }
                    
                    // Second pass: If all validations passed, apply the counters
                    if (validationPassed && validRateGroupIds.length > 0) {
                        validRateGroupIds.forEach(({ id, counter, rateGroup }) => {
                            // Set the counter value
                            this.form.counters[id] = counter;
                            
                            // Initialize pax details if contacts are required
                            if (this.has_contacts) {
                                this.form.paxDetails[id] = this.form.paxDetails[id] || [];
                                for (let i = 0; i < counter; i++) {
                                    this.form.paxDetails[id].push({ name: '', age: '', weight: '', note: '' });
                                }
                            }
                        });
                        
                        // Update total people selected
                        this.form.total_people_selected = Object.values(this.form.counters).reduce(
                            (sum, num) => sum + num,
                            0
                        );
                        
                        // Update pricing breakdown
                        this.updatePricingBreakdown();
                    }
                }
                
                // Display validation errors if any
                if (!validationPassed) {
                    // Stop the loader and show the page
                    this.autoSubmitInProgress = false;
                    
                    this.errors = [...validationErrors];
                    this.$nextTick(() => this.scrollToErrors());
                    
                    // Clear URL parameters from localStorage after validation failure
                    const clearedParams = { ...urlParams };
                    delete clearedParams.date;
                    delete clearedParams.timeSlot;
                    delete clearedParams.rateGroupIds;
                    localStorage.setItem('urlParams', JSON.stringify(clearedParams));
                    return;
                }
                
                // If all validations passed and we have both time slot and rate groups
                if (validationPassed && hasTimeSlot && hasRateGroups && matchingSlot) {
                    // Final validation: ensure we have selected people
                    const totalPeopleSelected = Object.values(this.form.counters).reduce((sum, num) => sum + num, 0);
                    
                    if (totalPeopleSelected > 0 && this.form.total > 0) {
                        // Check if package requires contact details for each passenger
                        if (this.has_contacts) {
                            // Don't auto-redirect if passenger details are required
                            // User needs to manually fill in contact information
                            console.error('Passenger contact details required. User must fill in details before continuing.');
                            
                            this.autoSubmitInProgress = false;
                            
                            // Clear URL parameters since they've been applied
                            const clearedParams = { ...urlParams };
                            delete clearedParams.date;
                            delete clearedParams.timeSlot;
                            delete clearedParams.rateGroupIds;
                            localStorage.setItem('urlParams', JSON.stringify(clearedParams));
                            
                            // Scroll to the contact form section so user can see it
                            this.$nextTick(() => {
                                const formElement = this.$refs.TourForm?.$el;
                                if (formElement) {
                                    formElement.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start'
                                    });
                                }
                            });
                        } else {
                            // No contact details required - auto-submit to checkout
                            // Clear URL parameters from localStorage
                            const clearedParams = { ...urlParams };
                            delete clearedParams.date;
                            delete clearedParams.timeSlot;
                            delete clearedParams.rateGroupIds;
                            localStorage.setItem('urlParams', JSON.stringify(clearedParams));
                            
                            // Automatically submit the form to proceed to checkout
                            this.processLoader(true);
                            await this.autoSubmitToCheckout();
                        }
                    } else {
                        // Stop the loader and show errors
                        this.autoSubmitInProgress = false;
                        
                        this.errors.push('Please select the number of guests to continue.');
                        this.$nextTick(() => this.scrollToErrors());
                    }
                } else {
                    this.autoSubmitInProgress = false;
                    
                    // Clear URL parameters from localStorage after applying them (one-time use)
                    const clearedParams = { ...urlParams };
                    delete clearedParams.date;
                    delete clearedParams.timeSlot;
                    delete clearedParams.rateGroupIds;
                    localStorage.setItem('urlParams', JSON.stringify(clearedParams));
                }
            } catch (error) {
                console.log('Failed to apply URL parameters:', error);
                
                // Stop the loader and show errors
                this.autoSubmitInProgress = false;
                
                this.errors.push('An error occurred while processing URL parameters.');
                this.$nextTick(() => this.scrollToErrors());
            }
        },

        async autoSubmitToCheckout() {
            this.autoSubmitInProgress = true;
            try {
                // Use common processing logic
                await this.processFormSubmission();
                
                if (this.errors.length) {
                    // Stop auto-submit and show errors
                    this.autoSubmitInProgress = false;
                    this.processLoader(false);
                    this.$nextTick(() => this.scrollToErrors());
                }
            } catch (error) {
                console.error('Auto-submit error:', error);
                
                // Stop the loader and show errors
                this.autoSubmitInProgress = false;
                this.processLoader(false);
                
                this.errors.push('An unexpected error occurred. Please try again.');
                this.$nextTick(() => this.scrollToErrors());
            }
        },

        navigateToTab(tab, destination) {
            if ([1, 3, 4, 5, 6].includes(tab)) {
                if (tab === 1 || Object.keys(this.cartItem).length) {
                    this.$store.dispatch('storeMindChange', 1);
                    this.$router.push({ name: destination });
                } else {
                    Swal.fire({
                        toast: true,
                        html: `In order to proceed with ${destination.toLowerCase()}, please schedule a trip.`,
                        icon: 'info'
                    });
                }
            }
        },

        roundout(amount, places = 2) {
            if (places < 0) places = 0;
            let factor = Math.pow(10, places);
            return Math.round(amount * factor) / factor;
        },

        processLoader(show) {
            if(!this.autoSubmitInProgress) {
                if (show) {
                    if (this.loaderInstance) this.loaderInstance.hide();
                    this.loaderInstance = this.$loading.show();
                } else if (this.loaderInstance) {
                    this.loaderInstance.hide();
                    this.loaderInstance = null;
                }
            }
        },

        scrollToErrors() {
            // Skip scrolling if auto-submit is in progress (refs are hidden)
            if (!this.errors.length) {
                return;
            }

            this.$nextTick(() => {
                if (this.$refs.TourForm?.$refs.packageErrorDisplay) {
                    this.$refs.TourForm.$refs.packageErrorDisplay.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'nearest'
                    });
                } else if (this.$refs.TourForm?.$el) {
                    // Fallback to scrolling to the TourForm element itself
                    this.$refs.TourForm.$el.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'nearest'
                    });
                } else {
                    console.warn('TourForm or packageErrorDisplay ref is undefined');
                    const tourFormElement = this.$refs.TourForm?.$el;
                    if (tourFormElement) {
                        tourFormElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                            inline: 'nearest'
                        });
                    }
                }
            });
        },

        getStartDate() {
            return getMomentTimezone(this.$store.state.timezone).format('YYYY-MM-DD');
        },

        getEndDate() {
            const date = getMomentTimezone(this.$store.state.timezone).year(getMomentTimezone(this.$store.state.timezone).year() + 1).month(11).date(31);
            date.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
            return date.format('YYYY-MM-DD');
        },

        isDisabled(slot) {
            return slot.bookable_status === 'Closed' || slot.dd >= slot.seats;
        },

        calculateTime(timeValue, hoursToAdd, minutesToAdd) {
            const time = getMomentTimezone(this.$store.state.timezone, `2000-01-01T${timeValue}`);
            time.add(hoursToAdd, 'hours');
            time.add(minutesToAdd, 'minutes');
            return time.format('HH:mm:ss');
        },

        openPhonePopup() {
            Swal.fire({
                toast: false,
                html: this.form.ctb_description,
                icon: 'info',
                allowOutsideClick: true,
                showConfirmButton: false,
                timer: 5000
            });
        }
    }
};
</script>

<style scoped>
/* Auto-submit full-page loader overlay */
.auto-submit-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 1);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
}

.auto-submit-message {
    text-align: center;
    padding: 2rem;
}

.spinner {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.auto-submit-message p {
    font-size: 1.2rem;
    color: #333;
    font-weight: 500;
}
</style>